import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { fetcher } from '~lib/api/fetch';
import {
  ITransformSales,
  Sales,
  SalesInfo,
  SalesResponse,
  SalesSource,
  SalesSourceKey,
  SalesSourceValue,
} from '~types/company';
import { getKeyByValue } from '~utils/common/object';
import { getSalaryRate } from '~utils/company/company';
import { parseToPercent } from '~utils/formatted/number';

import QueryKeys from '../queryKeys';

const url = (companyId?: string) => `/api/company/${companyId}/sales`;
export function useGetCompanySales(
  isClosed?: boolean,
  companyId?: string,
  selectedOption?: SalesSourceValue,
) {
  return useQuery<SalesResponse, AxiosError, ITransformSales>(
    [QueryKeys.companySales, companyId],
    () => fetcher(url(companyId)),
    {
      enabled: !isClosed && !!companyId,
      // @ts-ignore
      select: (response) => transformSales(response, selectedOption),
    },
  );
}

const transformSales = (data: SalesResponse, selectedOption?: SalesSourceValue) => {
  if (!data || !data.sales || !data.defaultSource) {
    return {
      activeSales: {
        perPerson: {
          industryRate: null,
          rateType: 'INDUSTRY',
          sales: null,
          totalRate: null,
        },
        salesSalaryRate: {
          industryRate: null,
          rateType: 'INDUSTRY',
          sales: null,
          totalRate: null,
        },
        total: {
          industryRate: null,
          rateType: 'INDUSTRY',
          sales: null,
          totalRate: null,
        },
        updatedAt: null,
      },
      defaultSource: null,
    };
  }
  const sourceOption = salesSource(data);
  const source: SalesSourceKey = selectedOption
    ? (getKeyByValue(SalesSource, selectedOption) as SalesSourceKey)
    : data.defaultSource;

  const activeSales = getActiveSales(data.sales, source);

  return {
    activeSales,
    defaultSource: activeSales.total.sales ? data.defaultSource : null,
    sourceOption,
  };
};

const getActiveSales = (data: SalesInfo, source: SalesSourceKey) => {
  const activeSales = data[source];

  return activeSales;
};
export const salesSource = (data: SalesResponse) => {
  const tempOptions = getAcitveSource(data.sales);

  const hasCorpSource = tempOptions.indexOf(SalesSource.CORP) > -1;

  const tempSource = Object.values(SalesSource).map((option) => {
    return {
      value: option,
      disabled: tempOptions.indexOf(option) < 0,
      selected: SalesSource[data.defaultSource] === option,
    };
  });

  // 기업제공이 없을때 tempSource[0]을 제거
  return hasCorpSource ? tempSource : tempSource.slice(1);
};

const getAcitveSource = (data: SalesInfo) => {
  const tempOptions: SalesSourceValue[] = [];
  const keys = Object.keys(data || {}) as SalesSourceKey[];
  keys.forEach(
    (source) =>
      data[source].total.sales && SalesSource[source] && tempOptions.push(SalesSource[source]),
  );

  return tempOptions;
};

export function salesDisplayPercent(sales: Sales, type: 'total' | 'salary' | 'person') {
  const rate = sales.rateType === 'INDUSTRY' ? sales.industryRate : sales.totalRate;

  if (typeof rate !== 'number') {
    return '-';
  }

  const percent = Math.round(parseToPercent(rate));

  if (type === 'salary' && sales.sales) {
    if (getSalaryRate(sales.sales) === 100) {
      return 1;
    }
  }
  if (percent === 0) {
    return 1;
  } else if (percent > 100) {
    return 100;
  } else {
    return percent;
  }
}
export function numberToKorean(num: number, type: 'total' | 'salary' | 'person') {
  function salesDisplayData(sales: number, unit: number) {
    const koreanNumber = (sales / unit).toFixed(1);

    return koreanNumber[koreanNumber.length - 1] === '0' || unit === 1.0e4
      ? koreanNumber.split('.')[0]
      : koreanNumber;
  }
  if (type === 'salary') {
    return getSalaryRate(num) + '%';
  }

  if (num >= 1.0e15) {
    return salesDisplayData(num, 1.0e15) + '경원';
  } else if (num >= 1.0e12) {
    return salesDisplayData(num, 1.0e12) + '조원';
  } else if (num >= 1.0e8) {
    return salesDisplayData(num, 1.0e8) + '억원';
  } else {
    return salesDisplayData(num, 1.0e4) + '만원';
  }
}
